import { useCallback, useEffect } from 'react';

const SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY;
const SCRIPT_ID = 'recaptcha-v3';

declare global {
  interface Window {
    grecaptcha?: {
      ready: (cb: () => void) => void;
      execute: (siteKey: string, opts: { action: string }) => Promise<string>;
    };
  }
}

// reCAPTCHA v3: loads the script once and returns an `execute(action)` that
// resolves to a token to send with the submit. When no site key is configured
// (dev), resolves to '' and the server skips the check in non-production.
export function useRecaptcha() {
  useEffect(() => {
    if (!SITE_KEY || document.getElementById(SCRIPT_ID)) return;
    const s = document.createElement('script');
    s.id = SCRIPT_ID;
    s.src = `https://www.google.com/recaptcha/api.js?render=${SITE_KEY}`;
    s.async = true;
    document.body.appendChild(s);
  }, []);

  const execute = useCallback(async (action: string): Promise<string> => {
    if (!SITE_KEY || !window.grecaptcha) return '';
    const grecaptcha = window.grecaptcha;
    return new Promise<string>((resolve) => {
      grecaptcha.ready(() => {
        grecaptcha
          .execute(SITE_KEY, { action })
          .then(resolve)
          .catch(() => resolve(''));
      });
    });
  }, []);

  return { execute };
}
